import pako from 'pako';

class DataLoaderService {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.progressCallbacks = new Map();
  }

  /**
   * Load compressed JSON data with progress tracking
   * @param {string} url - URL to the compressed JSON file
   * @param {function} onProgress - Progress callback function (progress: 0-100)
   * @returns {Promise<Object>} - Parsed JSON data
   */
  async loadCompressedJSON(url, onProgress) {
    // Check if data is already cached
    if (this.cache.has(url)) {
      if (onProgress) onProgress(100);
      return this.cache.get(url);
    }

    // Check if already loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const loadingPromise = this._performLoad(url, onProgress);
    this.loadingPromises.set(url, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(url, data);
      this.loadingPromises.delete(url);
      return data;
    } catch (error) {
      this.loadingPromises.delete(url);
      throw error;
    }
  }

  async _performLoad(url, onProgress) {
    try {
      // Initialize progress
      if (onProgress) onProgress(0);

      // Create a fetch request with progress tracking
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      let loaded = 0;
      const chunks = [];

      // Create a reader for progress tracking
      const reader = response.body.getReader();
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        // Update progress (0-70% for download)
        if (onProgress && total > 0) {
          const downloadProgress = Math.min((loaded / total) * 70, 70);
          onProgress(downloadProgress);
        }
      }

      // Update progress for decompression start
      if (onProgress) onProgress(75);

      // Combine all chunks
      const compressedData = new Uint8Array(loaded);
      let offset = 0;
      for (const chunk of chunks) {
        compressedData.set(chunk, offset);
        offset += chunk.length;
      }

      // Update progress for decompression
      if (onProgress) onProgress(85);

      // Decompress the data
      const decompressedData = pako.inflate(compressedData, { to: 'string' });
      
      // Update progress for JSON parsing
      if (onProgress) onProgress(95);

      // Parse JSON
      const jsonData = JSON.parse(decompressedData);
      
      // Complete
      if (onProgress) onProgress(100);

      return jsonData;
    } catch (error) {
      console.error('Error loading compressed JSON:', error);
      throw new Error(`Failed to load data: ${error.message}`);
    }
  }

  /**
   * Load bank data specifically
   * @param {function} onProgress - Progress callback function
   * @returns {Promise<Object>} - Bank data
   */
  async loadBankData(onProgress) {
    return this.loadCompressedJSON('/processed_bank_data.json.gz', onProgress);
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache status
   */
  getCacheStatus() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Create singleton instance
const dataLoader = new DataLoaderService();

export default dataLoader; 